<template lang="pug">
  #point
    ProgramSettingsFormWrapper(
      :form-data="formData"
      :keys-to-reset="keysToReset"
      :disable-save.sync="disableSave"
      title="Points Settings"
    )
      WtTile(parent vertical :col="12")
        WtTile(v-if="$auth.user().isAdmin" child :col="4")
          .columns.is-multiline.is-variable
            .column.is-narrow
              WtSwitchQuestionInput(
                label="Enable Points"
                :tooltip-text="enablePointsTooltip"
                v-model="points"
                name="enable points"
                @input="disableSave = false"
              )
        WtTile(v-if="points" child :col="4")
            WtTitle Reset Points
            BNotification(
              type="is-danger is-light"
              role="alert"
              has-icon
              icon="information"
              :closable="false"
            ) This will reset points for all users after selected date. Once points are reset the change cannot be reverted!
            .colums.is-multiline.is-variable 
              .column.is-narrow
                BField(label="Set date for points to be reset")
                  BDatepicker(
                    :min-date="new Date()"
                    icon="calendar-today"
                    editable
                    placeholder="Select Date"
                    v-model="dateSelection"
                    :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
                    position="is-top-right"
                    @input="disableSave = false"
                  )
                p.has-text-danger(v-if="resetPointsDate") {{ resetDateText }}

      WtTile(parent vertical :col="12" v-if="$auth.user().isAdmin" :padding="'small'")
        WtTitle Custom Point Uploader
        WtTile(child vertical :col="6" :padding="'small'") 
          BField(label="Download a Template").mb-2
          WtButton(
            color="primary"
            class="download-btn"
            :loading="false"
            @click.native="downloadTemplate"
          ) Download Template
            template(slot="icon")
              BIcon(icon="download")
        WtTile(child vertical :col="6" :padding="'small'") 
          BField(label="Upload a File")
        WtUploadFile(
          :multiple="multiple"
          accept="text/csv"
          :dragDrop="dragDrop"
          uploadIcon="upload"
          uploadIconSize="is-large"
          :loading="loading"
          :file-data="fileData"
          @save="fileUpload"
          @updateFile="updateFile"
          @deleteFile="deleteFile"
        )
          template(slot="custom-upload-lable") Drop your .csv file here or click on upload icon
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtTile from "@@/WtTile/WtTile";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtUploadFile from "@@/WtUploadFile/WtUploadFile";
import WtTooltip from "@@/WtTooltip/WtTooltip";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import ProgramSettingsFormWrapper from "@@/ProgramSettingsAdmin/ProgramSettingsFormWrapper";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";

export default {
  name: "PointsSettings",

  components: {
    WtTile,
    WtTooltip,
    WtButton,
    WtTitle,
    WtUploadFile,
    WtSwitchQuestionInput,
    ProgramSettingsFormWrapper
  },

  mixins: [dateUtilityMixin],

  data() {
    return {
      disableSave: true,
      dateSelection: null,
      multiple: false,
      dragDrop: true,
      loading: true,
      fileData: null
    };
  },

  computed: {
    ...mapGetters("program", ["programSettingsCopy", "coreProgramMeta"]),

    enablePointsTooltip() {
      return `
        Enable points for your program.
      `;
    },

    resetDateText() {
      // CREATE DATE OBJECTS IN ORDER TO COMPARE VIA < > OPERATORS
      let stringResetDate = this.date(
        this.resetPointsDate,
        "DATE_FORMAT_FOR_DATE_OBJ"
      ).formatted;
      let resetDateObj = new Date(stringResetDate);
      let today = new Date();

      // DISPLAY DATE AS TEXT FOR USERS
      let dateText = this.date(this.resetPointsDate, "DATE_FORMAT_FULL_MONTH")
        .formatted;

      // COMPARE FOR PAST VS FUTURE TENSE
      if (resetDateObj < today) {
        return `Points for all users were reset to zero on ` + dateText;
      } else {
        return `Points for all users will reset to zero on ` + dateText;
      }
    },

    formData() {
      return {
        points: Boolean(this.points),
        resetPointsDate: this.resetPointsDate
      };
    },

    points: {
      set(points) {
        this.updateProgramSettingsCopy({
          key: "points",
          value: points
        });
      },
      get() {
        return Number(this.programSettingsCopy.points);
      }
    },

    resetPointsDate: {
      set(resetPointsDate) {
        this.updateProgramSettingsCopy({
          key: "resetPointsDate",
          value: resetPointsDate
        });
      },
      get() {
        return this.programSettingsCopy.resetPointsDate;
      }
    },

    keysToReset() {
      return ["points", "resetPointsDate"];
    }
  },

  watch: {
    dateSelection(newValue) {
      let dateValue = this.date(newValue, "DATE_FORMAT_DB").formatted;
      this.resetPointsDate = dateValue;
    }
  },

  methods: {
    ...mapActions("program", [
      "updateProgramSettingsCopy",
      "uploadCustomPointCsv"
    ]),
    ...mapActions(["snackBar"]),
    objectToCsvRow(obj) {
      const values = Object.values(obj);
      const csvRow = values.map(value => `"${value}"`).join(",");
      return csvRow + "\n";
    },
    async downloadTemplate() {
      try {
        const data = [
          {
            Username: "user1",
            Points: 5,
            Points_Date: "2024-02-06"
          },
          {
            Username: "unicorn6",
            Points: 0,
            Points_Date: "2024-02-06"
          },
          {
            Username: "unicorn7",
            Points: 15,
            Points_Date: "2024-02-05"
          }
        ];
        let header = "UserName,Point,PointDate\n";
        const csvContent = header + data.map(this.objectToCsvRow).join("");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "CustomPointTemplate.csv";
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        this.snackBar({ message: error.message });
      }
    },
    deleteFile(index) {
      if (!index) {
        this.fileData = null;
      } else {
        this.fileData.splice(index, 1);
      }
    },
    updateFile(data) {
      this.fileData = data;
    },
    async fileUpload(data) {
      try {
        const programId = this.$auth.user().programId;
        const formData = new FormData();
        formData.append("file", data);
        const response = await this.uploadCustomPointCsv({
          programId,
          data: formData
        });
        if (response.success) {
          this.snackBar({ message: response.data.message });
          this.fileData = null;
        }
      } catch (error) {
        this.snackBar({
          type: "is-danger",
          duration: 10000,
          queue: false,
          message: `There was an error processing your file.
                        Please try again. Or please reach out to wellness@be-engaged.co.`
        });
      }
    }
  }
};
</script>

<style lang="sass">
#point
  .title:not(:last-child)
    margin-bottom : 0.2rem !important
  .box:not(:last-child)
    margin-bottom : 0.2rem !important
  .button .icon:first-child:last-child
    margin: 0.4rem 0.2rem 0.1rem 0.1rem
</style>
